/**
 * PlatformSolidity — the ONE answer to "can a body STAND on this platform?"
 *
 * WHY THIS EXISTS (2026-07-26): three copies of this rule had drifted apart,
 * and the gaps between them shipped a bug.
 *
 *   `CollisionResolver.getStrategyForPlatform`  skips rope/teleporter/cover/decorative
 *   `GroundCheckSystem.checkGrounded`           skipped rope/teleporter/cover + solid:false
 *   `MapSystem.validateOnGround` (spawn snap)   skipped rope ONLY
 *   `EntitySpawnerService.snapMobToNearestPlatform` (server) skips rope/teleporter + solid:false
 *
 * On `/combatlab` that cost a whole map's accuracy: its 2px `type:'decorative'`
 * facing-axis marker was invisible to collision but was BOTH a valid spawn
 * surface and valid ground, so the player spawned snapped to it at y=619 — 41px
 * above the floor he was designed to stand on — and stayed there. Every
 * distance the map advertised was measured from an anchor no player occupied.
 * (Measured live: `player.platformId === 'lab-facing-axis'`,
 * `getDefaultSpawnPosition() === {x:700, y:619}`.)
 *
 * ── WHAT THIS PREDICATE DELIBERATELY DOES *NOT* DO ─────────────────────────
 * It does not treat `type:'decorative'` as non-standable, even though
 * `CollisionResolver` does and even though that is almost certainly the
 * intent of the word "decorative". Making it implicit here is a GAMEPLAY
 * change on shipped content, not a cleanup — surveyed 2026-07-26:
 *
 *   drowned_road          resolved 2026-07-30: its 9 platform-drawn props were
 *                         replaced by non-platform scene props; only visibly
 *                         raised one-way slabs remain standable
 *   tidewend_outskirts    resolved 2026-07-30: farm and fence drawings were
 *                         moved out of platform collision; only real floors
 *                         and visibly raised one-way steps remain standable
 *   lanternwood           resolved 2026-07-30: platform-drawn landmarks were
 *                         removed; only real floors/one-way ledges remain
 *   BrawlArena / PvEArena / AbilityRangeTest   21 ruler ticks, 32px up
 *
 * Removing the remaining accidental standing surfaces from live quest maps
 * needs its own packet with its own visual QA, not a footnote in a test-map
 * fix. Until then
 * a map that wants a decoration to be a DRAWING says so explicitly with
 * `solid: false`, which every standing gate here (and the server's mob spawn
 * snapper) already honours.
 */

/** The minimal shape this predicate needs — accepts Platform and raw JSON. */
export interface PlatformSolidityInput {
  type?: string;
  solid?: boolean;
}

/**
 * Platform types that are sensors and can never be stood on:
 *  - `rope` / `teleporter` — sensors, handled by their own systems
 *  - `cover` — walkable visual occluder; blocks projectiles + LOS, not bodies
 *
 * `decorative` is NOT in this list — see the header. Author `solid: false`.
 */
export const SENSOR_PLATFORM_TYPES: ReadonlySet<string> = new Set([
  'rope',
  'teleporter',
  'cover',
]);

/**
 * True when a body may rest on this platform's top surface — the shared rule
 * for ground checks and for spawn/respawn/graveyard snapping, which had
 * diverged from each other.
 *
 * One-way platforms ARE standable (you land on top, you pass through from
 * below); that directional detail belongs to the caller, not here.
 */
export function isStandablePlatform(platform: PlatformSolidityInput | null | undefined): boolean {
  if (!platform) return false;
  if (platform.solid === false) return false;
  return !SENSOR_PLATFORM_TYPES.has(platform.type ?? '');
}
